import { Injectable, Inject } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { from, Observable, of, timer } from 'rxjs';
import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap } from 'rxjs/operators';
import { defaultFieldConfig } from './configs/default-field-config';
import { CustomEnvironment, environment, setApiOverrides } from '../../environments/environment';
import { FormFieldService } from '../services/form-field.service';
import { validate } from '../../../../../firstmac-validation-lib';
import { currencyFormatter, loadScript, mapErrors } from './utils/helpers';
import { PHONE_TYPE } from '../constants/phone-type.constant';
import { SalesService } from '../services/sales.service';

// function provided by firstmac-phone-validation-2.0.js
declare function fmcPhoneValidator(value: string, source: string, countryCode: string): Promise<any>;

/**
 * Creates a custom async validator for Angular forms
 * @param slug The slug for validation.
 * @param context
 * @returns An asynchronous validator function.
 */
export function fmcValidator(slug: string, context = {}): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => timer(300).pipe(
    switchMap(() => from(validate(slug, control.value, context, true)).pipe(
      map((errors: any) => (errors.length === 0 ? null : mapErrors(errors))),
    )),
  );
}
interface ValidationFeeResponse {
  valid: boolean;
  reasons?: any[];
  maximumFee?: number;
}

/**
 * Async Validator
 */
@Injectable({ providedIn: 'root' })
export class CustomAsyncValidator {
  constructor(
    private httpClient: HttpClient,
    private salesService: SalesService,
    @Inject(CustomEnvironment) customEnvironment: any,
  ) {
    setApiOverrides(customEnvironment);

    Promise.all([
      loadScript(`${environment.api.lcaDomain}/application/js/firstmac-phone-validation-2.0.js`),
      loadScript(`${environment.api.lcaDomain}/application/js/libphonenumber-max.min.js`),
    ]);
  }

  /**
   * Validator for postcode
   * @param fieldConfig(object) - Field Configuration(default: DriversLicenseCardNumberConfig)
   * @param outputParam(string) - Workbook output parameter(default: Justice/VX - Firstmac Category)
   * @param countryFieldName(string) - Optional
   * @param expectedOutputs (string[]) - Optional: Override the expected output validation
   * @returns ValidationErrors | null
   * @deprecated Use postcodeWorkbookValidator
   */
  postcodeValidator(
    fieldConfig: any = defaultFieldConfig.postCode,
    outputParam = 'Justice/VX - Firstmac Category',
    countryFieldName = null,
    expectedOutputs = null,
  ) {
    return (control: AbstractControl): Promise<ValidationErrors> | null => {
      let errors = {};

      // If config exist, then trigger validation using form field plugin
      const config = _.get(fieldConfig, 'config') || {};
      if (!_.isEmpty(config)) {
        // Validate form field using plugin
        errors = FormFieldService.validateFormField({ value: control.value }, config);

        // return if there's any existing error
        if (!_.isEmpty(errors)) {
          return Promise.resolve(errors);
        }
      }

      // Make sure that postcode has value before calling the workbook
      if (_.isEmpty(control.value)) {
        return Promise.resolve(null);
      }

      // Do not use the workbook if country is not AU;
      if (!_.isEmpty(countryFieldName)) {
        const countryFieldControl = control.parent.get(countryFieldName);
        const countryValue = countryFieldControl ? countryFieldControl.value : null;

        if (countryValue !== 'AU' && countryValue !== 13) {
          return Promise.resolve(null);
        }
      }

      // Validate postcode via workbook
      const validatePostcode = (postcode) => this.httpClient.post(`${environment.api.sales}/graph/calculators/postcode-categories-workbook`, { inputs: { Postcode: postcode } }).toPromise();

      return timer(500).pipe(
        switchMap(() => validatePostcode(control.value).then(
          (wbResult) => {
            const output = _.mapKeys(wbResult, (value, key) => _.trim(key));
            // get the value of output param
            const result = (output[outputParam] || '').toLowerCase();

            const invalidResult = ['not acceptable', 'no entry found', '0'];

            // if expectedOutputs is provided, override the default validation
            if (expectedOutputs) {
              if (!_.includes(expectedOutputs, result)) {
                return { postcode: true };
              }
            } else if (_.isEmpty(result) || invalidResult.includes(result)) {
              return { postcode: true };
            }

            // postcode is valid
            return null;
          },
          (err) => ({ error: true }),
        )),
      ).toPromise();
    };
  }

  /**
   * Creates a custom async validator to validate email address
   * @params httpClient The HttpClient instance
   * @returns An asynchronous validator function for email address.
   */
  emailValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const { value } = control;

      if (value === null || (typeof value === 'string' && !value.trim())) {
        return of(null);
      }

      return timer(1000).pipe(
        switchMap(() => from(validate('email', value))),
        switchMap((errors: any) => {
          // If errors exist from validation=lib, return them
          if (errors.length !== 0) {
            return of(mapErrors(errors));
          }

          const sensisVerifyEmail = (email) => this.httpClient.get(`${environment.api.lcaDomain}/retailapi/TotalCheckApi/api/Validation/email?email_address=${email}`);

          return sensisVerifyEmail(value).pipe(
            switchMap((response: any) => {
              if (response?.result?.domain_exists !== 'VALID' && response?.result?.email_valid !== 'VALID') {
                return of({ emailInvalid: true });
              }
              return of(null);
            }),
            catchError((error) => {
              console.error('Email verification error:', error);
              return of(null);
            }),
          );
        }),
      );
    };
  }

  /**
   * Creates a custom async validator to validate phone number
   * @param acceptedPhoneTypes An array of phone types that are considered valid. Default is ['MOBILE'].
   * @param rejectedPhoneTypes An array of phone types that are considered invalid.
   * @param countryCode The country code used for phone number validation. Default is 'AU'.
   * @param allowBypass A flag that allows bypassing the validation
   * @returns An asynchronous validator function.
   */
  phoneValidator(
    acceptedPhoneTypes: string[] = [PHONE_TYPE.MOBILE],
    rejectedPhoneTypes: string[] = [],
    countryCode: string = 'AU',
    allowBypass: boolean = false,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const { value } = control;

      if (value === null || (typeof value === 'string' && !value.trim())) {
        return of(null);
      }

      return timer(1000).pipe(
        switchMap(() => from(validate('phoneNumber', value))),
        switchMap((errors: any) => {
          // If errors exist from validation=lib, return them
          if (errors.length !== 0) {
            return of(mapErrors(errors));
          }

          return from(fmcPhoneValidator(value, 'sensis', countryCode)).pipe(
            map((response: any) => {
              const { valid, formatted, phoneType } = response || {};

              let validPhoneType = false;
              if (phoneType && acceptedPhoneTypes && rejectedPhoneTypes) {
                const acceptedPhoneType = (!acceptedPhoneTypes || acceptedPhoneTypes.length === 0 || acceptedPhoneTypes.includes(phoneType));
                const rejectedPhoneType = !!(rejectedPhoneTypes.length && rejectedPhoneTypes.includes(phoneType));
                validPhoneType = acceptedPhoneType && !rejectedPhoneType;
              }

              if (validPhoneType && formatted !== value) {
                control.patchValue(formatted, { emitEvent: false });
              }

              if (!valid && !allowBypass) {
                return { telno: true };
              }

              return null;
            }),
            catchError((error) => {
              console.error('Phone verification error:', error);
              return of(null);
            }),
          );
        }),
      );
    };
  }

  /**
   * Creates a custom async validator to validate postcode using validation lib & workbook
   * @param outputParam Workbook output parameter to be validated. Default is 'Justice/VX - Firstmac Category'.
   * @param expectedOutputs Optional: Override the expected output validation. Default is an empty array.
   * @param classification Optional: parameter to validate the ValEx classification output. Default is an empty array.
   * @param expectedParam
   * @returns An async validator function.
   */
  postcodeWorkbookValidator(
    outputParam = 'Justice/VX - Firstmac Category',
    expectedOutputs = [],
    allowPOBox = false,
    classifications = [],
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const { value } = control;

      if (value === null || (typeof value === 'string' && !value.trim())) {
        return of(null);
      }

      return timer(500).pipe(
        switchMap(() => from(validate('postcode', value, {}, true))),
        switchMap((errors: any) => {
          // If errors exist from validation=lib, return them
          if (errors.length !== 0) {
            return of(mapErrors(errors));
          }

          // Validate postcode via workbook
          const validatePostcode = (postcode) => this.httpClient.post(`${environment.api.sales}/graph/calculators/postcode-categories-workbook`, { inputs: { Postcode: postcode } }).toPromise();

          return timer(500).pipe(
            switchMap(() => validatePostcode(value).then(
              (wbResult) => {
                const output = _.mapKeys(wbResult, (value, key) => _.trim(key));

                const {
                  'ValEx classification': valexClassification,
                  'Broker Tools - Firstmac Category': brokertoolsCategory,
                } = output;

                let isPostCodeValid = false;
                const result = (output[outputParam] || '').toLowerCase();

                const INVALID_RESULTS = ['not acceptable', 'no entry found', '0'];
                if (!_.includes(INVALID_RESULTS, result)) {
                  isPostCodeValid = true;
                }

                // if expectedOutputs is not empty, validate the outputParam value
                if (!_.isEmpty(expectedOutputs) && _.includes(expectedOutputs, result)) {
                  isPostCodeValid = true;
                }

                if (allowPOBox && brokertoolsCategory === 'PO Boxes only') {
                  isPostCodeValid = true;
                }

                if (!_.isEmpty(classifications) && _.includes(classifications, valexClassification)) {
                  isPostCodeValid = true;
                }

                // postcode is valid
                return isPostCodeValid ? null : { postcode: true };
              },
              (err) => ({ error: true }),
            )),
          ).toPromise();
        }),
      );
    };
  }

  driversLicenceNumberValidator(stateFormControl: AbstractControl) {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const { value } = control;

      if (value === null || (typeof value === 'string' && !value.trim())) {
        return of(null);
      }

      const state = _.toLower(stateFormControl.value);
      return timer(1000).pipe(
        switchMap(() => from(validate(`drivers-licence-number-${state}`, value, {}, true))),
        switchMap((errors: any) => {
          if (errors.length !== 0) {
            return of(mapErrors(errors));
          }

          return of(null);
        }),
      );
    };
  }

  /**
   * Asynchronous validator function to validate fee-related data by calling a sales-api /fees/:feeType/validate.
   *
   * @param {AbstractControl} control - The form control containing the fee data.
   * @param {string} feeType - The type of fee being validated.
   * @returns {Observable<ValidationErrors | null>} - An observable emitting validation errors if invalid or null if valid.
   *
   * @example
   * const control = this.formBuilder.control({
   *   amount: 100,
   *   purpose: 'Some purpose',
   *   channel: 'tps',
   *   productCategoryType: 'motor-vehicle-loan',
   *   pipelineEffectiveDate: new Date(),
   * });
   * const feeType = 'origination-fee';
   * this.fmcFeeValidator(control, feeType).subscribe(errors => {
   *   if (errors) {
   *     console.log('Validation errors:', errors);
   *   } else {
   *     console.log('Validation succeeded');
   *   }
   * });
   */
  fmcFeeValidator(control: AbstractControl, feeType: string): Observable<ValidationErrors | null> {
    const {
      amount,
      fee,
      purpose,
      channel,
      productCategoryType,
      pipelineEffectiveDate = new Date(),
    } = control.value;

    const payload = {
      amount,
      fee,
      purpose,
      channel,
      productCategoryType,
      pipelineEffectiveDate,
    };

    return this.salesService.validateFee(feeType, payload)
      .pipe(
        map((response: ValidationFeeResponse) => {
          if (response.valid) {
            return null;
          }

          if (response.reasons && response.reasons.length > 0) {
            let message = null;
            if (response.reasons.includes('fee-exceeds-maximum')) {
              const maximumFee = currencyFormatter.format(response?.maximumFee);
              message = `Value must be less or equal to ${maximumFee}`;
            }

            return {
              errors: response.reasons,
              message,
            };
          }
        }),
        catchError(() => of(null)),
      );
  }
}
